/**
 * Sidebar search component with autocomplete dropdown.
 * Uses Ark UI Combobox for accessible autocomplete functionality.
 * Searches people and Spaces; empty focus shows personal + platform recents.
 */

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Combobox, Portal, createListCollection } from '@ark-ui/react';
import type { PublicIdentity, PublicSpace } from '@adieuu/shared';
import { useDirectorySearch } from '../hooks/useDirectorySearch';
import { useDirectoryRecentSearches } from '../hooks/useDirectoryRecentSearches';
import { usePlatformSearchSuggestions } from '../hooks/usePlatformSearchSuggestions';
import { useIdentity } from '../hooks/useIdentity';
import { useFriends } from '../hooks/useFriends';
import { useCipherStore } from '../hooks/useCipherStore';
import { getSpaceCipherLink } from '../services/spaceCipherService';
import { resolveSpaceDisplayName } from '../pages/spaces/spaceMetadataCipher';
import { useSidebar } from './Sidebar';
import { DirectoryRecentSearches } from './DirectoryRecentSearches';
import { Icon } from '../icons/Icon';
import { SpaceLogo } from './SpaceLogo';
import { Tooltip } from './Tooltip';

export interface SidebarSearchProps {
  /** Called when an identity is selected */
  onSelect?: (identity: PublicIdentity) => void;
  /** Override the default placeholder text */
  placeholderOverride?: string;
  /** Show social actions (add friend) - requires Identity and Friends providers. Defaults to true. */
  showSocialActions?: boolean;
}

type SidebarHit =
  | { kind: 'person'; key: string; identity: PublicIdentity }
  | { kind: 'space'; key: string; space: PublicSpace };

/**
 * Inner component that requires Identity + Friends providers.
 * Rendered only when showSocialActions is true.
 */
function AddFriendButton({ identityId }: { identityId: string }) {
  const { t } = useTranslation();
  const { identity: selfIdentity, status: identityStatus } = useIdentity();
  const { sendRequest, getFriendshipStatus } = useFriends();
  const isIdentityLoggedIn = identityStatus === 'logged_in' && selfIdentity;
  const [status, setStatus] = useState<'none' | 'friends' | 'pending_incoming' | 'pending_outgoing'>('none');
  const [isSending, setIsSending] = useState(false);

  useEffect(() => {
    if (!isIdentityLoggedIn || identityId === selfIdentity?.id) return;
    let cancelled = false;
    getFriendshipStatus(identityId).then((result) => {
      if (!cancelled) setStatus(result.status);
    });
    return () => { cancelled = true; };
  }, [identityId, isIdentityLoggedIn, selfIdentity?.id, getFriendshipStatus]);

  if (!isIdentityLoggedIn || identityId === selfIdentity?.id) return null;

  if (status === 'friends') {
    return (
      <span className="sidebar-search-item-friend-status sidebar-search-item-friend-status--friends">
        <Icon name="users" />
        {t('friends.alreadyFriends')}
      </span>
    );
  }

  if (status === 'pending_outgoing' || status === 'pending_incoming') {
    return (
      <span className="sidebar-search-item-friend-status sidebar-search-item-friend-status--pending">
        <Icon name="clock" />
        {t('friends.pending')}
      </span>
    );
  }

  return (
    <Tooltip content={t('friends.addFriend')}>
      <button
        type="button"
        className="sidebar-search-item-add-friend"
        disabled={isSending}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsSending(true);
          void sendRequest(identityId).then((ok) => {
            if (ok) setStatus('pending_outgoing');
            setIsSending(false);
          });
        }}
      >
        <Icon name="plus" />
        <span>{t('friends.addFriend')}</span>
      </button>
    </Tooltip>
  );
}

function SpaceHitLabel({ space }: { space: PublicSpace }) {
  const { t } = useTranslation();
  const { getCipherKey } = useCipherStore();
  const localCipherId = getSpaceCipherLink(space.id);
  const cipher = localCipherId ? getCipherKey(localCipherId) : null;
  return (
    <>
      {resolveSpaceDisplayName(space, cipher, {
        encryptedSpace: t('spaces.encryptedSpacePlaceholder'),
      })}
    </>
  );
}

export function SidebarSearch({ onSelect, placeholderOverride, showSocialActions = true }: SidebarSearchProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isExpanded, setExpanded, closeMobile } = useSidebar();
  const inputRef = useRef<HTMLInputElement>(null);
  const [pendingFocus, setPendingFocus] = useState(false);
  const { people, spaces, isSearching, search, clear, query } = useDirectorySearch({
    enableDiscover: false,
    searchLimit: 8,
    debounceMs: 300,
  });
  const { recents, add: addRecent, remove: removeRecent, clear: clearRecents } =
    useDirectoryRecentSearches();
  const [inputValue, setInputValue] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const emptyInput = inputValue.trim().length === 0;
  const { mode: platformMode, terms: platformTerms, isLoading: platformLoading } =
    usePlatformSearchSuggestions(isOpen && emptyInput);
  const platformLabelKey =
    platformMode === 'trending' ? 'search.platformTrending' : 'search.platformRecent';

  const hits = useMemo<SidebarHit[]>(() => {
    const personHits: SidebarHit[] = people.map((identity) => ({
      kind: 'person',
      key: `person:${identity.id}`,
      identity,
    }));
    const spaceHits: SidebarHit[] = spaces.map((space) => ({
      kind: 'space',
      key: `space:${space.id}`,
      space,
    }));
    return [...personHits, ...spaceHits];
  }, [people, spaces]);

  const collection = useMemo(
    () =>
      createListCollection({
        items: hits,
        itemToString: (item) =>
          item.kind === 'person' ? item.identity.displayName : item.space.name || item.space.slug,
        itemToValue: (item) => item.key,
      }),
    [hits],
  );

  const hasResults = people.length > 0 || spaces.length > 0;
  const showRecents =
    emptyInput && (recents.length > 0 || platformTerms.length > 0 || platformLoading);
  const showDropdown =
    isOpen && (showRecents || query.length >= 2 || isSearching);

  const goToDirectorySearch = useCallback(
    (q: string) => {
      const trimmed = q.trim().replace(/\s+/g, ' ');
      if (trimmed.length >= 2) {
        addRecent(trimmed);
      }
      setIsOpen(false);
      closeMobile();
      clear();
      setInputValue('');
      navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    },
    [addRecent, closeMobile, clear, navigate],
  );

  const handleInputChange = useCallback(
    (details: { inputValue: string }) => {
      setInputValue(details.inputValue);
      search(details.inputValue);
      const trimmed = details.inputValue.trim();
      setIsOpen(trimmed.length === 0 || trimmed.length >= 2);
    },
    [search],
  );

  const handleOpenChange = useCallback((details: { open: boolean }) => {
    setIsOpen(details.open);
  }, []);

  const handleSelect = useCallback(
    (details: { items: SidebarHit[] }) => {
      const selected = details.items[0];
      if (!selected) return;
      setIsOpen(false);
      closeMobile();
      clear();
      setInputValue('');
      if (selected.kind === 'person') {
        if (onSelect) {
          onSelect(selected.identity);
        } else {
          navigate(`/identity/${selected.identity.id}`);
        }
        return;
      }
      navigate(`/s/${selected.space.slug}`);
    },
    [closeMobile, clear, navigate, onSelect],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && query.length >= 2) {
        e.preventDefault();
        goToDirectorySearch(query);
      }
    },
    [query, goToDirectorySearch],
  );

  const handleFocus = useCallback(() => {
    if (inputValue.trim().length === 0) {
      setIsOpen(true);
    }
  }, [inputValue]);

  useEffect(() => {
    if (pendingFocus && isExpanded) {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      setPendingFocus(false);
    }
  }, [pendingFocus, isExpanded]);

  if (!isExpanded) {
    return (
      <Tooltip content={t('search.title')}>
        <button
          type="button"
          className="sidebar-search-collapsed"
          onClick={() => {
            setExpanded(true);
            setPendingFocus(true);
          }}
          aria-label={t('search.title')}
        >
          <Icon name="search" />
        </button>
      </Tooltip>
    );
  }

  return (
    <div className="sidebar-search">
      <Combobox.Root
        collection={collection}
        inputValue={inputValue}
        onInputValueChange={handleInputChange}
        onValueChange={handleSelect}
        open={showDropdown}
        onOpenChange={handleOpenChange}
        loopFocus
        openOnClick={false}
        selectionBehavior="clear"
      >
        <Combobox.Control className="sidebar-search-control">
          <span className="sidebar-search-icon">
            <Icon name="search" />
          </span>
          <Combobox.Input
            ref={inputRef}
            className="sidebar-search-input"
            placeholder={placeholderOverride ?? t('search.placeholder')}
            onKeyDown={handleKeyDown}
            onFocus={handleFocus}
          />
          {isSearching && <span className="sidebar-search-spinner spinner spinner-sm" />}
        </Combobox.Control>

        <Portal>
          <Combobox.Positioner className="sidebar-search-positioner">
            <Combobox.Content className="sidebar-search-content">
              {showRecents && (
                <div className="sidebar-search-recents">
                  <DirectoryRecentSearches
                    recents={recents}
                    onSelect={goToDirectorySearch}
                    onRemove={removeRecent}
                    onClear={clearRecents}
                  />
                  <DirectoryRecentSearches
                    recents={platformTerms}
                    onSelect={goToDirectorySearch}
                    labelKey={platformLabelKey}
                    editable={false}
                  />
                </div>
              )}

              {!hasResults && query.length >= 2 && !isSearching && (
                <div className="sidebar-search-empty">
                  {t('search.noResults')}
                </div>
              )}

              {people.length > 0 && (
                <div className="sidebar-search-group-label">{t('search.sections.people')}</div>
              )}
              {collection.items
                .filter((item): item is Extract<SidebarHit, { kind: 'person' }> => item.kind === 'person')
                .map((hit) => (
                  <Combobox.Item
                    key={hit.key}
                    item={hit}
                    className="sidebar-search-item"
                  >
                    <div className="sidebar-search-item-avatar">
                      {hit.identity.avatarUrl ? (
                        <img
                          src={hit.identity.avatarUrl}
                          alt=""
                          className="sidebar-search-item-avatar-img"
                        />
                      ) : (
                        <span className="sidebar-search-item-avatar-placeholder">
                          {hit.identity.displayName.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div className="sidebar-search-item-info">
                      <Combobox.ItemText className="sidebar-search-item-name">
                        {hit.identity.displayName}
                      </Combobox.ItemText>
                      <span className="sidebar-search-item-username">
                        @{hit.identity.username}
                      </span>
                    </div>
                    {showSocialActions && (
                      <AddFriendButton identityId={hit.identity.id} />
                    )}
                  </Combobox.Item>
                ))}

              {spaces.length > 0 && (
                <div className="sidebar-search-group-label">{t('search.sections.spaces')}</div>
              )}
              {collection.items
                .filter((item): item is Extract<SidebarHit, { kind: 'space' }> => item.kind === 'space')
                .map((hit) => (
                  <Combobox.Item
                    key={hit.key}
                    item={hit}
                    className="sidebar-search-item"
                  >
                    <div className="sidebar-search-item-avatar sidebar-search-item-avatar--space">
                      <SpaceLogo space={hit.space} name={hit.space.name || hit.space.slug} size="md" />
                    </div>
                    <div className="sidebar-search-item-info">
                      <Combobox.ItemText className="sidebar-search-item-name">
                        <SpaceHitLabel space={hit.space} />
                      </Combobox.ItemText>
                      <span className="sidebar-search-item-username">
                        /s/{hit.space.slug}
                      </span>
                    </div>
                  </Combobox.Item>
                ))}

              {query.length >= 2 && (
                <button
                  type="button"
                  className="sidebar-search-view-all"
                  onClick={() => goToDirectorySearch(query)}
                >
                  {t('search.viewAll')}
                </button>
              )}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.Root>
    </div>
  );
}
